import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    this.updatePriceFromVariant(event.detail.resource);
  };

  /**
   * Falls back to updating from the variant payload when the fetched HTML
   * does not replace the price correctly.
   * @param {VariantUpdateEvent['detail']['resource']} variant
   */
  updatePriceFromVariant(variant) {
    if (!variant || typeof variant.price !== 'number') return;

    const { priceContainer, volumePricingNote } = this.refs;
    if (!priceContainer || volumePricingNote) return;

    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    const currencyCode = this.dataset.currencyCode || 'USD';
    const price = formatMoney(variant.price, moneyFormat, currencyCode);
    const compareAtPrice =
      typeof variant.compare_at_price === 'number' && variant.compare_at_price > variant.price
        ? formatMoney(variant.compare_at_price, moneyFormat, currencyCode)
        : null;
    const showSalePriceFirst = this.dataset.showSalePriceFirst === 'true';

    if (!compareAtPrice) {
      priceContainer.innerHTML = `<span class="price">${price}</span>`;
      return;
    }

    const salePriceMarkup = `
      <span role="group">
        <span class="visually-hidden">Sale price&nbsp;</span>
        <span class="price">${price}</span>
      </span>
    `;
    const regularPriceMarkup = `
      <span role="group">
        <span class="visually-hidden">Regular price&nbsp;</span>
        <span class="compare-at-price">${compareAtPrice}</span>
      </span>
    `;

    priceContainer.innerHTML = showSalePriceFirst
      ? `${salePriceMarkup}${regularPriceMarkup}`
      : `${regularPriceMarkup}${salePriceMarkup}`;
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
